import type { WebSearchProduct } from "@/api/models/api-types";

export interface ExternalChatLink {
  url: string;
  label: string;
}

const nonEmptyString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value : null;

/** Yalnız tarayıcının güvenle sayfa veya görsel olarak açabileceği URL'ler. */
export const isOpenableUrl = (value: unknown): value is string => {
  const text = nonEmptyString(value);
  if (!text) return false;

  try {
    const { protocol } = new URL(text);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
};

/** Güvenilmeyen SSE/history external-link payload'ını ortak şekle daraltır. */
export const parseExternalChatLink = (
  value: unknown,
): ExternalChatLink | null => {
  if (typeof value !== "object" || value === null) return null;

  const raw = value as { url?: unknown; label?: unknown };
  const label = nonEmptyString(raw.label);
  if (!isOpenableUrl(raw.url) || !label) return null;

  return { url: raw.url, label };
};

/** Aynı URL'i yalnız bir kez tutar; live ve history aynı sırayı üretir. */
export const parseExternalChatLinks = (
  value: unknown,
): ExternalChatLink[] => {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  const links: ExternalChatLink[] = [];
  value.forEach((item) => {
    const link = parseExternalChatLink(item);
    if (!link || seen.has(link.url)) return;
    seen.add(link.url);
    links.push(link);
  });
  return links;
};

/** Güvenilmeyen SSE/history web-card payload'ını widget ile aynı sözleşmeye daraltır. */
export const parseWebSearchProducts = (value: unknown): WebSearchProduct[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (typeof item !== "object" || item === null) return [];
    const raw = item as Record<string, unknown>;
    const externalId = nonEmptyString(raw.externalId);
    const title = nonEmptyString(raw.title);
    const currency = nonEmptyString(raw.currency);
    const source = nonEmptyString(raw.source);
    const position = Number(raw.position);
    const price = raw.price === null ? null : Number(raw.price);

    if (
      !externalId ||
      !title ||
      !currency ||
      !source ||
      !isOpenableUrl(raw.imageUrl) ||
      !isOpenableUrl(raw.productUrl) ||
      !Number.isFinite(position) ||
      (price !== null && !Number.isFinite(price))
    ) {
      return [];
    }

    const rating = raw.rating === null ? null : Number(raw.rating);
    const ratingCount =
      raw.ratingCount === null ? null : Number(raw.ratingCount);

    return [
      {
        externalId,
        title,
        price,
        currency,
        imageUrl: raw.imageUrl,
        productUrl: raw.productUrl,
        source,
        rating: Number.isFinite(rating) ? rating : null,
        ratingCount: Number.isFinite(ratingCount) ? ratingCount : null,
        position,
      },
    ];
  });
};
